import React, { useState, useEffect, useRef } from 'react';

const API = '';

const PRIORITY_FILTERS = [
  { key: 'CRITICAL', label: 'Critical', threshold: [200, Infinity], color: '#ef4444', bg: '#fef2f2', border: '#fecaca', dot: '#ef4444' },
  { key: 'HIGH',     label: 'High',     threshold: [110, 200],      color: '#f97316', bg: '#fff7ed', border: '#fed7aa', dot: '#f97316' },
  { key: 'MEDIUM',   label: 'Medium',   threshold: [60, 110],       color: '#eab308', bg: '#fefce8', border: '#fde68a', dot: '#eab308' },
  { key: 'LOW',      label: 'Low',      threshold: [0, 60],         color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', dot: '#9ca3af' },
];

const PENDING_ISSUE_FILTERS = [
  { key: 'MAIN_LINK_DOWN', label: 'Main Link Down', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', matches: /^main\s*link\s*down\b/i },
  { key: 'MAIN_LINK_LOSS_HIGH', label: 'Main Link Loss High', color: '#c2410c', bg: '#fff7ed', border: '#fed7aa', matches: /^main\s*link\s*(?:loss\s*high|high\s*loss)\b/i },
  { key: 'FAT_DOWN', label: 'FAT Down', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', matches: /^fat\s*down\b/i },
  { key: 'FAT_LOSS_HIGH', label: 'FAT Loss High', color: '#0369a1', bg: '#f0f9ff', border: '#bae6fd', matches: /^fat\s*(?:loss\s*high|high\s*loss)\b/i },
];
const LONG_RUNNING_DAYS = 7;
const LONG_RUNNING_HOURS = LONG_RUNNING_DAYS * 24;
const LONG_RUNNING_ISSUE_KEYS = new Set(['MAIN_LINK_DOWN', 'FAT_DOWN']);
const FOLLOWUP_ISSUE_KEYS = new Set(['MAIN_LINK_DOWN', 'FAT_DOWN']);

function getPriorityKey(p) {
  if (p >= 200) return 'CRITICAL';
  if (p >= 110) return 'HIGH';
  if (p >= 60)  return 'MEDIUM';
  return 'LOW';
}

function getPendingIssueKey(issueType) {
  const issue = String(issueType || '').trim();
  return PENDING_ISSUE_FILTERS.find(filter => filter.matches.test(issue))?.key || null;
}

function PriorityBadge({ priority }) {
  const pk = getPriorityKey(priority);
  const cfg = PRIORITY_FILTERS.find(f => f.key === pk);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`,
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot, display: 'inline-block' }} />
      {cfg.label.toUpperCase()}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    'Active':           { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
    'Bill Expired':     { bg: '#fefce8', color: '#ca8a04', border: '#fde68a' },
    'Expiring Soon':    { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
    'Service Required': { bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff' },
    'Inactive':         { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' },
  };
  const s = map[status] || { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' };
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, padding: '2px 9px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
      {status}
    </span>
  );
}

export default function ODBSearchPage() {
  const [query, setQuery]           = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [result, setResult]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [downBoxes, setDownBoxes]   = useState([]);
  const [downLoading, setDownLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null); // null = all (for down alert dashboard)
  const [issueFilter, setIssueFilter] = useState(null); // null = all pending issue types
  const [longRunningOnly, setLongRunningOnly] = useState(false);
  const [savingFollowupTaskId, setSavingFollowupTaskId] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [userStatusFilter, setUserStatusFilter] = useState(null); // null = all users
  const debounceRef = useRef(null);
  const token   = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchActiveDowns = () => {
    fetch(`${API}/api/odb-active-downs`, { headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) { setDownBoxes(data); setLastRefreshed(new Date()); } setDownLoading(false); })
      .catch(() => setDownLoading(false));
  };

  useEffect(() => {
    fetchActiveDowns();
    // Auto-refresh every 60 seconds so counters update when BMFT tickets are completed
    const interval = setInterval(fetchActiveDowns, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(() => {
      fetch(`${API}/api/odb-box-autocomplete?q=${encodeURIComponent(val)}`, { headers })
        .then(r => r.json())
        .then(d => setSuggestions(d.suggestions || []));
    }, 300);
  };

  const doSearch = (name) => {
    const q = (name || query).trim();
    if (!q) return;
    setLoading(true); setError(''); setResult(null); setSuggestions([]);
    fetch(`${API}/api/odb-box-search?q=${encodeURIComponent(q)}`, { headers })
      .then(r => r.json())
      .then(d => { d.ok === false ? setError(d.error || 'Search failed') : setResult(d); setLoading(false); })
      .catch(() => { setError('Search failed'); setLoading(false); });
  };

  const isLongRunningPrimaryFault = (box) =>
    LONG_RUNNING_ISSUE_KEYS.has(getPendingIssueKey(box.issue_type)) && Number(box.duration_hours || 0) >= LONG_RUNNING_HOURS;
  const isFollowupEligibleFault = (box) => FOLLOWUP_ISSUE_KEYS.has(getPendingIssueKey(box.issue_type));
  const longRunningBoxes = downBoxes.filter(isLongRunningPrimaryFault);
  const longRunningCounts = {
    MAIN_LINK_DOWN: longRunningBoxes.filter(box => getPendingIssueKey(box.issue_type) === 'MAIN_LINK_DOWN').length,
    FAT_DOWN: longRunningBoxes.filter(box => getPendingIssueKey(box.issue_type) === 'FAT_DOWN').length,
  };

  // Followed Main Link/FAT tickets move below untouched tickets in their dedicated and long-running views.
  const filteredBoxes = downBoxes
    .filter(box =>
      (!activeFilter || getPriorityKey(box.priority) === activeFilter) &&
      (!issueFilter || getPendingIssueKey(box.issue_type) === issueFilter) &&
      (!longRunningOnly || isLongRunningPrimaryFault(box))
    )
    .sort((a, b) => {
      const followupOrderingActive = longRunningOnly || FOLLOWUP_ISSUE_KEYS.has(issueFilter);
      if (followupOrderingActive) {
        const followedOrder = Number(Boolean(String(a.followup_status || '').trim())) - Number(Boolean(String(b.followup_status || '').trim()));
        if (followedOrder) return followedOrder;
      }
      return longRunningOnly
        ? (Number(b.duration_hours || 0) - Number(a.duration_hours || 0)) || (Number(b.impacted_count || 0) - Number(a.impacted_count || 0)) || (Number(b.priority || 0) - Number(a.priority || 0))
        : 0;
    });

  const saveTicketFollowup = async (box, status) => {
    if (!isFollowupEligibleFault(box)) return;
    setSavingFollowupTaskId(box.task_id);
    try {
      const response = await fetch(`${API}/api/odb-active-down-followup`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: box.task_id, status })
      });
      if (!response.ok) throw new Error('Follow-up save failed');
      fetchActiveDowns();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingFollowupTaskId('');
    }
  };

  const counts = {
    CRITICAL: downBoxes.filter(b => b.priority >= 200).length,
    HIGH:     downBoxes.filter(b => b.priority >= 110 && b.priority < 200).length,
    MEDIUM:   downBoxes.filter(b => b.priority >= 60  && b.priority < 110).length,
    LOW:      downBoxes.filter(b => b.priority < 60).length,
  };
  const pendingIssueCounts = Object.fromEntries(
    PENDING_ISSUE_FILTERS.map(filter => [filter.key, downBoxes.filter(box => getPendingIssueKey(box.issue_type) === filter.key).length])
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 24px' }}>

        {/* ── Page Header ── */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{ width: 36, height: 36, background: '#1e40af', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📦</div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a' }}>ODB / FAT Box Search</h1>
          </div>
          <p style={{ margin: '0 0 0 46px', color: '#64748b', fontSize: 13 }}>Search by box name to view status, connected users, and billing info</p>
        </div>

        {/* ── Search Bar ── */}
        <div style={{ position: 'relative', marginBottom: 32 }}>
          <div style={{ display: 'flex', gap: 10, background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', padding: 6 }}>
            <input
              value={query}
              onChange={handleInput}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
              placeholder="Type box name e.g. SPK-FAT-C01, PK-FAT-C18, PL-FAT-D01..."
              style={{ flex: 1, padding: '10px 14px', border: 'none', outline: 'none', fontSize: 14, background: 'transparent', color: '#0f172a' }}
            />
            <button onClick={() => doSearch()} style={{
              padding: '10px 24px', background: '#1d4ed8', color: '#fff', border: 'none',
              borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              Search
            </button>
          </div>
          {suggestions.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 100, marginTop: 6, overflow: 'hidden' }}>
              {suggestions.map(s => (
                <div key={s} onClick={() => { setQuery(s); setSuggestions([]); doSearch(s); }}
                  style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 14, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #f1f5f9' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                  <span style={{ color: '#94a3b8' }}>📦</span> {s}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Down Alert Dashboard ── */}
        {!result && (
          <>
            {/* Section title */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 4, height: 20, background: '#ef4444', borderRadius: 2 }} />
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Active Down Alert Dashboard</h2>
                {!downLoading && (
                  <span style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                    {downBoxes.length} DOWN
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {lastRefreshed && (
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>Updated {lastRefreshed.toLocaleTimeString()}</span>
                )}
                <button onClick={() => { setDownLoading(true); fetchActiveDowns(); }} style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#2563eb', fontWeight: 600 }}>
                  🔄 Refresh
                </button>
                {(activeFilter || issueFilter || longRunningOnly) && (
                  <button onClick={() => { setActiveFilter(null); setIssueFilter(null); setLongRunningOnly(false); }} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#475569', fontWeight: 600 }}>
                    ✕ Clear Filter
                  </button>
                )}
              </div>
            </div>

            {downLoading ? (
              <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 14 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
                Loading down boxes...
              </div>
            ) : downBoxes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, background: '#f0fdf4', borderRadius: 16, color: '#166534', border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>All boxes are ONLINE</div>
              </div>
            ) : (
              <>
                {/* ── Priority Filter Cards (clickable) ── */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
                  {PRIORITY_FILTERS.map(f => (
                    <button key={f.key} onClick={() => setActiveFilter(activeFilter === f.key ? null : f.key)}
                      style={{
                        background: activeFilter === f.key ? f.bg : '#fff',
                        border: `2px solid ${activeFilter === f.key ? f.color : '#e2e8f0'}`,
                        borderRadius: 12, padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
                        boxShadow: activeFilter === f.key ? `0 0 0 3px ${f.color}22` : '0 1px 3px rgba(0,0,0,0.06)',
                        transition: 'all 0.15s',
                      }}>
                      <div style={{ fontSize: 26, fontWeight: 800, color: f.color, lineHeight: 1 }}>{counts[f.key]}</div>
                      <div style={{ fontSize: 12, color: f.color, fontWeight: 600, marginTop: 4 }}>{f.label}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                        {f.key === 'CRITICAL' ? '≥ 200 pts' : f.key === 'HIGH' ? '110–199 pts' : f.key === 'MEDIUM' ? '60–109 pts' : '< 60 pts'}
                      </div>
                    </button>
                  ))}
                  {/* Total card */}
                  <button onClick={() => { setActiveFilter(null); setIssueFilter(null); setLongRunningOnly(false); }}
                    style={{
                      background: !activeFilter ? '#eff6ff' : '#fff',
                      border: `2px solid ${!activeFilter ? '#3b82f6' : '#e2e8f0'}`,
                      borderRadius: 12, padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
                      boxShadow: !activeFilter ? '0 0 0 3px #3b82f622' : '0 1px 3px rgba(0,0,0,0.06)',
                      transition: 'all 0.15s',
                    }}>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#3b82f6', lineHeight: 1 }}>{downBoxes.length}</div>
                    <div style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600, marginTop: 4 }}>Total DOWN</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>All priorities</div>
                  </button>
                </div>

                {/* ── Pending Infra Issue Summary (clickable, read-only) ── */}
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 14, padding: 14, marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>Pending Infra Repairs</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Open tickets only; completed, cancelled, and closed tickets are excluded.</div>
                    </div>
                    <button onClick={() => { setIssueFilter(null); setActiveFilter(null); setLongRunningOnly(false); }} style={{ background: !issueFilter && !longRunningOnly ? '#dbeafe' : '#fff', color: '#1d4ed8', border: `1px solid ${!issueFilter && !longRunningOnly ? '#93c5fd' : '#dbeafe'}`, borderRadius: 9, padding: '7px 10px', cursor: 'pointer', textAlign: 'left' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1 }}>{downBoxes.length}</div>
                      <div style={{ fontSize: 10, fontWeight: 700, marginTop: 3 }}>TOTAL PENDING</div>
                    </button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
                    {PENDING_ISSUE_FILTERS.map(filter => (
                      <button key={filter.key} onClick={() => { setIssueFilter(issueFilter === filter.key ? null : filter.key); setLongRunningOnly(false); }} style={{ background: issueFilter === filter.key ? filter.bg : '#fff', border: `2px solid ${issueFilter === filter.key ? filter.color : filter.border}`, borderRadius: 10, padding: '11px 12px', cursor: 'pointer', textAlign: 'left', boxShadow: issueFilter === filter.key ? `0 0 0 2px ${filter.color}22` : 'none' }}>
                        <div style={{ fontSize: 22, lineHeight: 1, fontWeight: 800, color: filter.color }}>{pendingIssueCounts[filter.key]}</div>
                        <div style={{ fontSize: 11, lineHeight: 1.25, fontWeight: 700, color: '#334155', marginTop: 5 }}>{filter.label}</div>
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 3 }}>Pending / active</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── Long-running primary-fault priority view (clickable, read-only) ── */}
                <button onClick={() => { setLongRunningOnly(!longRunningOnly); setIssueFilter(null); setActiveFilter(null); }} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, background: longRunningOnly ? '#fff7ed' : '#fff', border: `2px solid ${longRunningOnly ? '#f97316' : '#fed7aa'}`, borderRadius: 14, padding: '14px 16px', marginBottom: 20, cursor: 'pointer', textAlign: 'left', boxShadow: longRunningOnly ? '0 0 0 3px #f9731622' : '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#9a3412' }}>Long-running Priority — Main Link + FAT Down</div>
                    <div style={{ fontSize: 11, color: '#9a3412', marginTop: 4 }}>Open for ≥ {LONG_RUNNING_DAYS} days. Sorted oldest first, then impacted users and existing priority.</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 5 }}>Main Link Down: {longRunningCounts.MAIN_LINK_DOWN} · FAT Down: {longRunningCounts.FAT_DOWN}</div>
                  </div>
                  <div style={{ minWidth: 88, textAlign: 'right' }}>
                    <div style={{ fontSize: 28, lineHeight: 1, fontWeight: 800, color: '#ea580c' }}>{longRunningBoxes.length}</div>
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#c2410c', marginTop: 4 }}>{longRunningOnly ? 'PRIORITY ACTIVE' : 'LONG-RUNNING'}</div>
                  </div>
                </button>

                {/* ── Down Boxes Table ── */}
                <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  {/* Table header */}
                  <div style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                      {longRunningOnly ? `Long-running Main Link + FAT Down (≥ ${LONG_RUNNING_DAYS} days)` : activeFilter ? `${PRIORITY_FILTERS.find(f=>f.key===activeFilter)?.label} boxes` : issueFilter ? `${PENDING_ISSUE_FILTERS.find(f=>f.key===issueFilter)?.label} tickets` : 'All DOWN boxes'} — {filteredBoxes.length} results
                    </span>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                          {['Priority', 'Box Name', 'Issue', 'Township', 'Users', 'Down Since', 'Duration', 'Ticket ID', 'Follow-up', ''].map(h => (
                            <th key={h} style={{ padding: '10px 16px', textAlign: h === 'Users' ? 'center' : 'left', color: '#64748b', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBoxes.map((box, i) => (
                          <tr key={box.task_id + i}
                            style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.1s' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                            <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                              <PriorityBadge priority={box.priority} />
                            </td>
                            <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                              <button onClick={() => { setQuery(box.link_name); doSearch(box.link_name); }}
                                style={{ background: 'none', border: 'none', color: '#1d4ed8', fontWeight: 700, cursor: 'pointer', fontSize: 13, padding: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ color: '#94a3b8' }}>📦</span>
                                {box.link_name}
                              </button>
                            </td>
                            <td style={{ padding: '12px 16px', color: '#374151', maxWidth: 160 }}>{box.issue_type || '—'}</td>
                            <td style={{ padding: '12px 16px', color: '#64748b', whiteSpace: 'nowrap' }}>
                              {box.township && box.township !== 'Other' ? box.township : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <span style={{
                                background: box.impacted_count > 50 ? '#fef2f2' : box.impacted_count > 10 ? '#fff7ed' : '#f9fafb',
                                color: box.impacted_count > 50 ? '#dc2626' : box.impacted_count > 10 ? '#ea580c' : '#6b7280',
                                padding: '3px 10px', borderRadius: 20, fontWeight: 700, fontSize: 12,
                                border: `1px solid ${box.impacted_count > 50 ? '#fecaca' : box.impacted_count > 10 ? '#fed7aa' : '#e5e7eb'}`,
                              }}>
                                {box.impacted_count}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', color: '#64748b', whiteSpace: 'nowrap', fontSize: 12 }}>
                              {box.down_since ? new Date(box.down_since).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </td>
                            <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                              <span style={{
                                background: box.duration_hours >= 24 ? '#fef2f2' : '#fff7ed',
                                color: box.duration_hours >= 24 ? '#dc2626' : '#ea580c',
                                padding: '3px 10px', borderRadius: 20, fontWeight: 700, fontSize: 12,
                                border: `1px solid ${box.duration_hours >= 24 ? '#fecaca' : '#fed7aa'}`,
                              }}>
                                {box.duration_label}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 12, fontFamily: 'monospace' }}>{box.task_id}</td>
                            <td style={{ padding: '12px 16px', minWidth: 120 }}>
                              {isFollowupEligibleFault(box) ? (
                                <select value={box.followup_status || ''} disabled={savingFollowupTaskId === box.task_id} onChange={e => saveTicketFollowup(box, e.target.value)} style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: 6, padding: '5px 7px', background: '#fff', color: '#334155', fontSize: 11, cursor: savingFollowupTaskId === box.task_id ? 'wait' : 'pointer', opacity: savingFollowupTaskId === box.task_id ? 0.65 : 1 }}>
                                  <option value="">Not followed</option>
                                  <option value="followed">Followed</option>
                                </select>
                              ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <button onClick={() => { setQuery(box.link_name); doSearch(box.link_name); }}
                                style={{ background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                View →
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* ── Loading / Error ── */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
            Searching...
          </div>
        )}
        {error && (
          <div style={{ background: '#fef2f2', color: '#dc2626', padding: '14px 18px', borderRadius: 10, border: '1px solid #fecaca', fontSize: 14 }}>
            ⚠️ {error}
          </div>
        )}

        {/* ── Search Results ── */}
        {result && (
          <div>
            <button onClick={() => { setResult(null); setQuery(''); }}
              style={{ marginBottom: 20, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
              ← Back to Down Alert Dashboard
            </button>

            {/* Box Status Card */}
            <div style={{
              background: '#fff', borderRadius: 14, border: `2px solid ${result.box_status === 'DOWN' ? '#fecaca' : '#bbf7d0'}`,
              padding: 24, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: result.down_info ? 20 : 0 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: result.box_status === 'DOWN' ? '#fef2f2' : '#f0fdf4',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
                }}>
                  {result.box_status === 'DOWN' ? '🔴' : '🟢'}
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{result.box_name}</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <span style={{
                      fontWeight: 700, fontSize: 13, padding: '2px 10px', borderRadius: 20,
                      background: result.box_status === 'DOWN' ? '#fef2f2' : '#f0fdf4',
                      color: result.box_status === 'DOWN' ? '#dc2626' : '#16a34a',
                      border: `1px solid ${result.box_status === 'DOWN' ? '#fecaca' : '#bbf7d0'}`,
                    }}>
                      {result.box_status}
                    </span>
                    {result.active_tasks > 0 && (
                      <span style={{ fontSize: 12, color: '#64748b' }}>{result.active_tasks} active ticket{result.active_tasks > 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
              </div>
              {result.down_info && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                  {[
                    ['Ticket', result.down_info.ticket_id],
                    ['Issue', result.down_info.issue_type],
                    ['Status', result.down_info.status],
                    ['Assigned', result.down_info.assigned_to || 'Unassigned'],
                    ['Down Since', result.down_info.down_since ? new Date(result.down_info.down_since).toLocaleString() : '—'],
                    ['Impacted', `${result.down_info.impacted_count} users`],
                  ].map(([label, val]) => (
                    <div key={label} style={{ background: '#fef2f2', borderRadius: 10, padding: '10px 14px' }}>
                      <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
                      <div style={{ fontWeight: 600, color: '#0f172a', marginTop: 3, fontSize: 13 }}>{val}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Summary counters — clickable to filter users */}
            {result.summary && (
              <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
                {[
                  { label: 'Total', count: result.summary.total, color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe', filterKey: null },
                  { label: 'Active', count: result.summary.active, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', filterKey: 'Active' },
                  { label: 'Bill Expired', count: result.summary.bill_expired, color: '#ca8a04', bg: '#fefce8', border: '#fde68a', filterKey: 'Bill Expired' },
                  { label: 'Expiring Soon', count: result.summary.expiring_soon, color: '#ea580c', bg: '#fff7ed', border: '#fed7aa', filterKey: 'Expiring Soon' },
                  { label: 'Needs Service', count: result.summary.needs_service, color: '#9333ea', bg: '#fdf4ff', border: '#e9d5ff', filterKey: 'Service Required' },
                  { label: 'Inactive', count: result.summary.inactive, color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', filterKey: 'Inactive' },
                ].map(s => {
                  const isActive = s.filterKey === null ? userStatusFilter === null : userStatusFilter === s.filterKey;
                  return (
                    <button key={s.label}
                      onClick={() => setUserStatusFilter(isActive && s.filterKey !== null ? null : s.filterKey)}
                      style={{
                        background: s.bg,
                        border: isActive ? `2px solid ${s.color}` : `1px solid ${s.border}`,
                        borderRadius: 12, padding: '12px 18px', minWidth: 100,
                        cursor: 'pointer', textAlign: 'left',
                        boxShadow: isActive ? `0 0 0 3px ${s.color}22` : 'none',
                        transform: isActive ? 'translateY(-1px)' : 'none',
                        transition: 'all 0.15s',
                      }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.count}</div>
                      <div style={{ fontSize: 11, color: s.color, fontWeight: 600, marginTop: 2 }}>{s.label}</div>
                      {isActive && s.filterKey !== null && (
                        <div style={{ fontSize: 10, color: s.color, marginTop: 2, opacity: 0.7 }}>✓ filtered</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Users table */}
            {result.users && result.users.length > 0 && (() => {
              const filteredUsers = userStatusFilter
                ? result.users.filter(u => u.display_status === userStatusFilter)
                : result.users;
              return (
              <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>Connected Users</span>
                  <span style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '1px 8px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{filteredUsers.length}</span>
                  {userStatusFilter && (
                    <>
                      <span style={{ fontSize: 12, color: '#64748b' }}>— filtered by <strong>{userStatusFilter}</strong></span>
                      <button onClick={() => setUserStatusFilter(null)} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '1px 8px', cursor: 'pointer', fontSize: 11, color: '#64748b' }}>✕ Clear</button>
                    </>
                  )}
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        {['#', 'Service ID', 'Customer Name', 'Phone', 'Status', 'Billing Expiry', 'Tickets'].map(h => (
                          <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 12 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u, i) => (
                        <tr key={u.service_id + i} onClick={() => setSelectedUser(u)}
                          style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                          <td style={{ padding: '11px 16px', color: '#cbd5e1', fontSize: 12 }}>{i + 1}</td>
                          <td style={{ padding: '11px 16px', fontWeight: 600, color: '#1d4ed8', fontFamily: 'monospace', fontSize: 12 }}>{u.service_id}</td>
                          <td style={{ padding: '11px 16px', fontWeight: 500, color: '#0f172a' }}>{u.customer_name || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={{ padding: '11px 16px', color: '#64748b' }}>{u.phone || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={{ padding: '11px 16px' }}><StatusBadge status={u.display_status} /></td>
                          <td style={{ padding: '11px 16px', fontSize: 12 }}>
                            {u.billing_expiry ? (
                              <span style={{ color: u.days_until_expiry < 0 ? '#dc2626' : u.days_until_expiry < 7 ? '#d97706' : '#16a34a' }}>
                                {new Date(u.billing_expiry).toLocaleDateString('en-GB')}
                                {u.days_until_expiry !== null && u.days_until_expiry !== undefined && (
                                  <span style={{ marginLeft: 5, color: '#94a3b8', fontSize: 11 }}>
                                    ({u.days_until_expiry < 0 ? `${Math.abs(u.days_until_expiry)}d ago` : `${u.days_until_expiry}d`})
                                  </span>
                                )}
                              </span>
                            ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                          </td>
                          <td style={{ padding: '11px 16px' }}>
                            {u.open_tickets && u.open_tickets.length > 0 ? (
                              <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '2px 8px', borderRadius: 20, fontWeight: 700, fontSize: 12 }}>
                                {u.open_tickets.length}
                              </span>
                            ) : <span style={{ color: '#cbd5e1', fontSize: 12 }}>0</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* ── Customer Side Panel ── */}
      {selectedUser && (
        <>
          {/* Backdrop */}
          <div onClick={() => setSelectedUser(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 999 }} />
          {/* Panel */}
          <div style={{
            position: 'fixed', top: 0, right: 0, width: 360, height: '100vh',
            background: '#fff', zIndex: 1000, overflowY: 'auto',
            boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
            display: 'flex', flexDirection: 'column',
          }}>
            {/* Panel header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>Customer Details</span>
              <button onClick={() => setSelectedUser(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>✕</button>
            </div>
            <div style={{ padding: '20px 24px', flex: 1 }}>
              {/* Customer info */}
              <div style={{ background: '#f8fafc', borderRadius: 12, padding: '16px', marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', marginBottom: 4 }}>{selectedUser.customer_name || 'Unknown Customer'}</div>
                <div style={{ color: '#64748b', fontSize: 12, fontFamily: 'monospace', marginBottom: 10 }}>{selectedUser.service_id}</div>
                <StatusBadge status={selectedUser.display_status} />
              </div>
              {/* Details */}
              <div style={{ marginBottom: 20 }}>
                {[
                  ['Phone', selectedUser.phone || '—'],
                  ['Billing Expiry', selectedUser.billing_expiry ? new Date(selectedUser.billing_expiry).toLocaleDateString('en-GB') : '—'],
                  ['Days Remaining', selectedUser.days_until_expiry !== null && selectedUser.days_until_expiry !== undefined
                    ? (selectedUser.days_until_expiry < 0 ? `Expired ${Math.abs(selectedUser.days_until_expiry)}d ago` : `${selectedUser.days_until_expiry} days`)
                    : '—'],
                  ['Open Tickets', selectedUser.open_tickets ? selectedUser.open_tickets.length : 0],
                ].map(([label, val]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ color: '#64748b', fontSize: 13 }}>{label}</span>
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>{val}</span>
                  </div>
                ))}
              </div>
              {/* Open tickets list */}
              {selectedUser.open_tickets && selectedUser.open_tickets.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Open Tickets</div>
                  {selectedUser.open_tickets.map((t, i) => (
                    <div key={i} style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '10px 12px', marginBottom: 6, fontSize: 12 }}>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>{t.ticket_id}</div>
                      <div style={{ color: '#64748b', marginTop: 2 }}>{t.issue_type} · {t.status}</div>
                    </div>
                  ))}
                </div>
              )}
              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selectedUser.phone && (
                  <a href={`tel:${selectedUser.phone}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#1d4ed8', color: '#fff', padding: '12px', borderRadius: 10, fontWeight: 600, textDecoration: 'none', fontSize: 14 }}>
                    📞 Call Customer
                  </a>
                )}
                <a href={`/customers?search=${selectedUser.service_id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#f8fafc', color: '#374151', border: '1px solid #e2e8f0', padding: '11px', borderRadius: 10, fontWeight: 600, textDecoration: 'none', fontSize: 14 }}>
                  👤 View Full Profile
                </a>
                <a href={`/service-ops?customer=${selectedUser.service_id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', padding: '11px', borderRadius: 10, fontWeight: 600, textDecoration: 'none', fontSize: 14 }}>
                  🎫 Create Ticket
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
